// PayTree payments, created and read by this storefront directly at the
// provider (src/lib/payment-gateway/paytree/direct-client.ts). There is no
// other rail: no intermediary service sits on the money path, and no env var
// selects between rails any more.
//
// Hardcoded, not NEXT_PUBLIC_*: Next.js 16 Turbopack doesn't reliably inline
// NEXT_PUBLIC_* into the checkout client bundle.
//
// There are deliberately NO credentials in this file: this module is also
// imported by checkout client components. The provider's API url and token are
// server-only env values read by the direct client.

import { PAYTREE_ACTIVE_METHOD_IDS } from "./paytree-active-methods";

/** Eurolabs runs a single Saleor channel. */
const PAYTREE_ENABLED_CHANNELS = ["standard"];

/** Namespaced (`paytree_*`) so a method id always names its rail. */
export type PaytreeMethodId = "paytree_card" | "paytree_paypal" | "paytree_klarna";
/** The `method` value PayTree's API expects. */
export type PaytreeApiMethod = "card" | "paypal" | "klarna";

export type PaytreeMethod = {
	id: PaytreeMethodId;
	apiMethod: PaytreeApiMethod;
	labelKey: string;
	descriptionKey: string;
	iconKey: string;
};

// Order = UI order. PayTree's full method enum is larger (card, crypto, wire,
// paypal, klarna, ideal, bancontact, trustly, kakaopay, p24, blik); only the
// ones we would ever offer are listed here.
export const PAYTREE_METHODS: PaytreeMethod[] = [
	{
		id: "paytree_card",
		apiMethod: "card",
		labelKey: "checkout.payment.paytreeCardTitle",
		descriptionKey: "checkout.payment.paytreeCardDescription",
		iconKey: "card",
	},
	{
		// Not offered since 2026-11-04; kept so earlier PayPal payments still settle.
		// Its checkout translations and logo were removed with it.
		id: "paytree_paypal",
		apiMethod: "paypal",
		labelKey: "checkout.payment.paypal",
		descriptionKey: "checkout.payment.paytreePaypalDescription",
		iconKey: "paypal",
	},
	{
		id: "paytree_klarna",
		apiMethod: "klarna",
		labelKey: "checkout.payment.klarna",
		descriptionKey: "checkout.payment.paytreeCardDescription",
		iconKey: "klarna",
	},
];

/** What is offered for new payments; see `./paytree-active-methods` (currently nothing). */
export function getActivePaytreeMethods(
	activeIds: readonly PaytreeMethodId[] = PAYTREE_ACTIVE_METHOD_IDS,
): PaytreeMethod[] {
	return PAYTREE_METHODS.filter((method) => activeIds.includes(method.id));
}

export function isPayTreeMethod(value: unknown): value is PaytreeMethodId {
	return typeof value === "string" && PAYTREE_METHODS.some((method) => method.id === value);
}

export function isActivePaytreeMethod(value: unknown): value is PaytreeMethodId {
	return typeof value === "string" && getActivePaytreeMethods().some((method) => method.id === value);
}

/**
 * Start limits per method: EUR only, and the confirmed PayPal route limits for
 * `paytree_paypal` should PayPal ever be re-activated. Only new starts check
 * this; it is never applied to historical settlements.
 */
export function isPaytreeAmountSupported(method: PaytreeMethodId, amount: number, currency: string): boolean {
	if (currency !== "EUR" || !Number.isFinite(amount) || amount <= 0) return false;
	return method !== "paytree_paypal" || (amount >= 1 && amount <= 5_000);
}

export function paytreeApiMethod(id: PaytreeMethodId): PaytreeApiMethod {
	const method = PAYTREE_METHODS.find((m) => m.id === id);
	if (!method) throw new Error(`[PayTree] Unknown method id: ${id}`);
	return method.apiMethod;
}

/**
 * Which PayTree tiles to render.
 *
 * Three gates, in this order:
 *
 *  1. `channel` — always first. PayTree never opens a channel it was not
 *     configured for.
 *  2. `available` — whether the PayTree rail is open for this checkout
 *     (`visiblePaymentRails`: its Saleor app is offered, and the rail is
 *     switched on, or previewed in this session and configured). It can only
 *     ever REMOVE the tile, never add it. Defaults to unavailable so an unknown
 *     answer falls back to another offered payment method.
 *  3. `enabled` can hide the allowed methods, never enable a paused method.
 *
 * There is no per-session override any more: the checkout's rail preview
 * (`?rails=`) replaced the former `?paytree=0/1` switch.
 */
export function getVisiblePaytreeMethods(
	channel: string,
	available: boolean = false,
	enabled: boolean = true,
): PaytreeMethod[] {
	if (!PAYTREE_ENABLED_CHANNELS.includes(channel)) return [];
	if (!available) return [];
	return enabled ? getActivePaytreeMethods() : [];
}
